import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import DashboardLayout from "../layouts/dashboard";
import { StaggerContainer, StaggerItem } from "../../interface/shared/animations";
import StockTable, { StockTableSkeleton } from "../../interface/shared/stocktable";
import { useStockDetail } from "../../api/stock";
import { useStockRelocation } from "../../api/stockrelocation";
import { ProviderStockLocations } from "../../api/stocklocations";
import RelocationDialog from "./relocation-dialog";
import { ProductStock, StockRelocationRequest } from "../../shared/types";

export default function StockDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { stockItem, relatedStock, relocationRequests, errors, loading, fetchStockDetail } = useStockDetail(id);
    const { cancelRelocation, submitting: cancelling } = useStockRelocation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("Relocation request submitted successfully.");
    const [cancelConfirmId, setCancelConfirmId] = useState<number | null>(null);

    return (
        <DashboardLayout>
            <ProviderStockLocations>
                <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 3 }}>
                        <Button
                            startIcon={<ArrowBackRoundedIcon />}
                            onClick={() => navigate("/stock")}
                        >
                            Back
                        </Button>

                        {stockItem && (
                            <Button
                                variant="outlined"
                                startIcon={<SwapHorizRoundedIcon />}
                                onClick={() => setDialogOpen(true)}
                            >
                                Request Move
                            </Button>
                        )}
                    </Stack>

                    {errors.length > 0 && (
                        <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                            action={
                                <Button color="inherit" size="small" onClick={fetchStockDetail}>
                                    Retry
                                </Button>
                            }
                        >
                            {errors.join(" ")}
                        </Alert>
                    )}

                    {loading ? <_DetailsSkeleton /> : stockItem && (
                        <StaggerContainer>
                            <StaggerItem>
                                <_StockSummary stock={stockItem} />
                            </StaggerItem>

                            <StaggerItem>
                                <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                    Other stock for {stockItem.product.name} ({relatedStock.length})
                                </Typography>

                                <StockTable
                                    stock={relatedStock}
                                    emptyMessage="No other stock found for this product."
                                />
                            </StaggerItem>

                            {relocationRequests.length > 0 && (
                                <StaggerItem>
                                    <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
                                        Pending Relocation Requests ({relocationRequests.length})
                                    </Typography>

                                    <_RelocationRequestsTable
                                        requests={relocationRequests}
                                        onCancel={(id) => setCancelConfirmId(id)}
                                    />
                                </StaggerItem>
                            )}
                        </StaggerContainer>
                    )}
                </Box>

                {stockItem && (
                    <RelocationDialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        onSuccess={() => {
                            setSnackbarMessage("Relocation request submitted successfully.");
                            setSnackbarOpen(true);
                            fetchStockDetail();
                        }}
                        stockItem={stockItem}
                    />
                )}

                <Dialog
                    open={cancelConfirmId !== null}
                    onClose={() => setCancelConfirmId(null)}
                >
                    <DialogTitle>Cancel Relocation Request</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to cancel this relocation request? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setCancelConfirmId(null)} disabled={cancelling}>
                            No, keep it
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={cancelling}
                            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : undefined}
                            onClick={async () => {
                                if (cancelConfirmId === null) return;
                                const success = await cancelRelocation(cancelConfirmId);
                                if (success) {
                                    setCancelConfirmId(null);
                                    setSnackbarMessage("Relocation request cancelled.");
                                    setSnackbarOpen(true);
                                    fetchStockDetail();
                                }
                            }}
                        >
                            {cancelling ? "Cancelling..." : "Yes, cancel it"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarOpen(false)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert severity="success" onClose={() => setSnackbarOpen(false)} variant="filled">
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </ProviderStockLocations>
        </DashboardLayout>
    );
}

function _StockSummary({ stock }: { stock: ProductStock }) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h6">
                            {stock.product.code} &middot; {stock.location.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {stock.product.name}
                        </Typography>
                    </Box>
                </Stack>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Location</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.location.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Level</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.level}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Warehouse</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.location.warehouse.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Quantity</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.quantity}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Weight</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.weight} kg</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Expires</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.expires_at != null ? stock.expires_at.toLocaleDateString() : '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <Typography variant="caption" color="text.secondary">Shelf Life</Typography>
                        <Typography variant="body1" fontWeight={500}>{stock.shelf_life != null ? `${stock.shelf_life.toFixed(0)} days` : '-'}</Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function _RelocationRequestsTable({
    requests,
    onCancel,
}: {
    requests: StockRelocationRequest[];
    onCancel: (id: number) => void;
}) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Location</TableCell>
                            <TableCell>Level</TableCell>
                            <TableCell>Requested By</TableCell>
                            <TableCell>Requested At</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requests.map((req) => (
                            <TableRow key={req.id}>
                                <TableCell>
                                    {req.location.name} ({req.location.warehouse.name})
                                </TableCell>
                                <TableCell>{req.level}</TableCell>
                                <TableCell>{req.user.name}</TableCell>
                                <TableCell>
                                    {req.created_at.toLocaleDateString()} {req.created_at.toLocaleTimeString()}
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => onCancel(req.id)}
                                    >
                                        Cancel
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

function _DetailsSkeleton() {
    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    <Skeleton variant="text" width={200} height={32} />
                    <Skeleton variant="text" width={150} height={20} sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Grid key={i} size={{ xs: 6, sm: 2 }}>
                                <Skeleton variant="text" width={80} height={16} />
                                <Skeleton variant="text" width={100} height={24} />
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
            <Skeleton variant="text" width={200} height={28} sx={{ mt: 4, mb: 2 }} />
            <StockTableSkeleton />
        </>
    );
}
